/**
 * Utility functions for formatting documents (cedula, passport, etc.)
 */

/**
 * Check if a document looks like a Dominican cedula (11 digits)
 * @param {string} value - The document value
 * @returns {boolean}
 */
export const isCedulaFormat = (value) => {
  const numbers = value.replace(/\D/g, '');
  return numbers.length === 11;
};

/**
 * Check if a document looks like a passport (alphanumeric, usually 6-9 chars)
 * @param {string} value - The document value
 * @returns {boolean}
 */
export const isPassportFormat = (value) => {
  // Passports have letters and numbers, usually 6-9 characters
  const cleaned = value.replace(/\s/g, '').toUpperCase();
  return /^[A-Z0-9]{6,12}$/.test(cleaned) && /[A-Z]/.test(cleaned);
};

/**
 * Format a Dominican cedula (XXX-XXXXXXX-X)
 * @param {string} value - The raw cedula value
 * @returns {string} - Formatted cedula
 */
export const formatCedula = (value) => {
  // Remove everything except numbers
  const numbers = value.replace(/\D/g, '');
  
  // Apply format
  if (numbers.length <= 3) {
    return numbers;
  } else if (numbers.length <= 10) {
    return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
  } else {
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 10)}-${numbers.slice(10, 11)}`;
  }
};

/**
 * Format a passport (uppercase, trimmed)
 * @param {string} value - The raw passport value
 * @returns {string} - Formatted passport
 */
export const formatPassport = (value) => {
  return value.replace(/\s/g, '').toUpperCase();
};

/**
 * Smart format a document - detects if it's a cedula or passport and formats accordingly
 * @param {string} value - The raw document value
 * @param {string} type - 'cedula', 'passport', or 'auto' (default)
 * @returns {string} - Formatted document
 */
export const formatDocument = (value, type = 'auto') => {
  if (!value) return '';
  
  if (type === 'passport') {
    return formatPassport(value);
  }
  
  if (type === 'cedula') {
    return formatCedula(value);
  }
  
  // Auto-detect: if it contains only numbers or looks like cedula, format as cedula
  const hasLetters = /[A-Za-z]/.test(value);
  
  if (hasLetters) {
    return formatPassport(value);
  } else {
    return formatCedula(value);
  }
};

/**
 * Validate a Dominican cedula
 * @param {string} value - The cedula to validate
 * @returns {boolean}
 */
export const validateCedula = (value) => {
  const numbers = value.replace(/\D/g, '');
  return numbers.length === 11;
};

/**
 * Validate a passport
 * @param {string} value - The passport to validate
 * @returns {boolean}
 */
export const validatePassport = (value) => {
  const cleaned = value.replace(/\s/g, '');
  return cleaned.length >= 5 && cleaned.length <= 12;
};

/**
 * Validate a document (cedula or passport)
 * @param {string} value - The document to validate
 * @param {string} type - 'cedula', 'passport', or 'auto'
 * @returns {boolean}
 */
export const validateDocument = (value, type = 'auto') => {
  if (!value || value.length < 5) return false;
  
  if (type === 'passport') {
    return validatePassport(value);
  }
  
  if (type === 'cedula') {
    return validateCedula(value);
  }
  
  // Auto: valid if it's a valid cedula OR a valid passport
  const hasLetters = /[A-Za-z]/.test(value);
  
  if (hasLetters) {
    return validatePassport(value);
  } else {
    return validateCedula(value);
  }
};
